import type { SignInCredential, SignUpCredential } from '@/@types/auth'
import appConfig from '@/configs/app.config'
import { apiSignIn, apiSignUpAccountant } from '@/services/AuthService'
import { setUser, signInSuccess, signOutSuccess, useAppDispatch, useAppSelector } from '@/store'
import { useQueryClient } from '@tanstack/react-query'
import { jwtDecode } from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'

type Status = 'success' | 'failed'

function useAuth() {
   const { t } = useTranslation()
   const dispatch = useAppDispatch()

   const queryClient = useQueryClient()

   const navigate = useNavigate()

   const query = useQuery()

   const { token, fromAccountantToken, signedIn, isAccountant } = useAppSelector((state) => state.auth.session)

   const signIn = async (
      values: SignInCredential,
   ): Promise<
      | {
           status: Status
           message: string
        }
      | undefined
   > => {
      try {
         const resp = await apiSignIn(values)
         if (resp.data) {
            const { accessToken, refreshToken } = resp.data

            const profile = jwtDecode(accessToken) as any
            queryClient.clear()
            dispatch(signInSuccess({ access_token: accessToken, refresh_token: refreshToken, isAccountant: resp.data.isAccountant }))
            navigate(resp.data.isAccountant ? appConfig.accountantAuthenticatedEntryPath : appConfig.companyAuthenticatedEntryPath)

            if (profile) {
               dispatch(
                  setUser({
                     fullName: profile.full_name,
                     partyIdentification: profile.party_identification,
                     email: profile.email,
                     userName: values.userName,
                     onlyBackup: JSON.parse(profile.only_backup),
                     authority: [],
                     isAccountant: resp.data.isAccountant,
                  }),
               )
            }

            setTimeout(() => {
               navigate(resp.data.isAccountant ? appConfig.accountantAuthenticatedEntryPath : appConfig.companyAuthenticatedEntryPath)
            }, 300)
            return {
               status: 'success',
               message: '',
            }
         }
         // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      } catch (errors: any) {
         if (errors?.response?.data?.error_description === 'invalid_username_or_password') {
            return {
               status: 'failed',
               message: t('signin.invalid_username_or_password'),
            }
         } else {
            return {
               status: 'failed',
               message: errors?.response?.data?.message || errors.toString(),
            }
         }
      }
   }

   const signUp = async (
      values: SignUpCredential,
   ): Promise<
      | {
           status: Status
           message: string
        }
      | undefined
   > => {
      try {
         const resp = await apiSignUpAccountant(values)

         if (resp.status === 201) {
            return {
               status: 'success',
               message: 'Kayıt başarılı',
            }
         } else {
            return {
               status: 'failed',
               message: (resp as any).data?.message,
            }
         }

         // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      } catch (errors: any) {
         if (errors?.response?.data?.error_description === 'invalid_username_or_password') {
            return {
               status: 'failed',
               message: t('signin.invalid_username_or_password'),
            }
         } else {
            return {
               status: 'failed',
               message: errors?.response?.data?.message || errors.toString(),
            }
         }
      }
   }

   const handleSignOut = () => {
      queryClient.clear()
      dispatch(signOutSuccess())
      dispatch(
         setUser({
            avatar: '',
            userName: '',
            email: '',
            roles: [],
            isAccountant: undefined,
         }),
      )
      navigate(appConfig.unAuthenticatedEntryPath)
   }

   const signOut = async () => {
      // await apiSignOut()
      handleSignOut()
   }

   return {
      authenticated: token && signedIn,
      isAccountant: signedIn && token && isAccountant,
      signOut,
      signIn,
      signUp,
      fromAccountantToken,
   }
}

export default useAuth
